
import React, { useState, useEffect, useCallback } from "react"
import { Row, Col, Card, Progress, Typography, Spin, Alert, Empty } from "antd"
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend, Tooltip } from "recharts"
import styled from "styled-components"
import { useDispatch, useSelector } from "react-redux"

// Import Redux actions and selectors for practice data
import {
  fetchPracticeScores,
  fetchAptitudePracticeScores,
  fetchPracticeScores2,
  fetchAptitudePracticeScores2,
} from "../../../actions"

import {
  selectPracticeScores,
  selectPracticeScoresLoading,  
  selectPracticeScoresError,
  selectAptitudePracticeScores2,
  selectAptitudePracticeScoresLoading,
  selectAptitudePracticeScoresError,
  selectCommunicationDayZero,
  selectAptitudeDayZero,
  // Add actual assessment scores selectors
  selectAssessmentScores2,
  selectAssessmentScores2Loading,
  selectAssessmentScores2Error,
  selectAptitudeAssessmentScores2,
  selectAptitudeAssessmentScores2Loading,
  selectAptitudeAssessmentScores2Error,
  selectCommunicationAssessmentDayZero,
  selectAptitudeAssessmentDayZero,
} from "../../../selectors"

const { Title, Text } = Typography

// Error Boundary Component for Chart
class ChartErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true }
  }

  componentDidCatch(error, errorInfo) {
    console.error("Chart rendering error:", error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          padding: '40px 20px', 
          textAlign: 'center' 
        }}>
          <div style={{ fontSize: '16px', fontWeight: '600', color: '#ff4d4f', marginBottom: '4px' }}>
            Chart rendering error
          </div>
          <div style={{ fontSize: '14px', color: '#8c8c8c' }}>
            Unable to display progress chart. Please try refreshing the page.
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

// Styled Components with consistent design
const TabContent = styled.div`
  padding: 24px;
  min-height: 500px;
`

const SectionCard = styled(Card)`
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
  background: white;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    transform: translateY(-2px);
  }

  .ant-card-head {
    border-bottom: 1px solid #f0f0f0;
    background: #fafafa;
    border-radius: 8px 8px 0 0;
    padding: 12px 20px;
    min-height: auto;
  }

  .ant-card-head-title {
    font-size: 16px;
    font-weight: 600;
    color: #333333;
  }

  .ant-card-body {
    padding: 20px;
  }
`

const RolePillsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const RolePill = styled.button`
  border-radius: 16px;
  background: #f0f8ff;
  border: 1px solid #d6e7ff;
  color: #1890ff;
  font-size: 12px;
  font-weight: 500;
  height: 28px;
  padding: 0 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #e6f4ff;
    border-color: #91caff;
    transform: translateY(-1px);
  }
`

const ReadinessSection = styled.div`
  text-align: center;
  padding: 20px;
  background: white;
  border-radius: 8px;
`

const ReadinessCircle = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: conic-gradient(
    #0FB680 0deg ${(props) => (props.score / 100) * 360}deg,
    #f5f5f5 ${(props) => (props.score / 100) * 360}deg 360deg
  );
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  position: relative;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);

  &::before {
    content: '';
    position: absolute;
    width: 90px;
    height: 90px;
    background: white;
    border-radius: 50%;
  }
`

const ReadinessText = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
`

const ReadinessScore = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: #333333;
  line-height: 1.2;
`

const ReadinessStatus = styled(Text)`
  font-size: 12px;
  color: ${(props) => (props.ready ? "#0FB680" : "#F54B56")};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 16px;
  margin-top: 16px;
`

const SkillCard = styled(Card)`
  border-radius: 8px;
  text-align: center;
  background: white;
  border: 1px solid #f0f0f0;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    transform: translateY(-2px);
  }

  .ant-card-body {
    padding: 16px 12px;
  }
`

const SkillTitle = styled(Text)`
  font-size: 12px;
  font-weight: 500;
  color: #262626;
  display: block;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const SkillLevel = styled(Text)`
  font-size: 12px;
  font-weight: 500;
  color: #262626;
  display: block;
  margin-top: 8px;
`

const SkillImprovement = styled(Text)`
  font-size: 11px;
  color: ${(props) => (props.positive ? "#52c41a" : "#8c8c8c")};
  display: block;
  margin-top: 2px;
`

const ChartSection = styled(Card)`
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);
  margin-top: 24px;
  background: white;

  .ant-card-head {
    border-bottom: 1px solid #f0f0f0;
    background: #fafafa;
    border-radius: 8px 8px 0 0;
    padding: 12px 20px;
    min-height: auto;
  }

  .ant-card-head-title {
    font-size: 16px;
    font-weight: 600;
    color: #333333;
  }

  .ant-card-body {
    padding: 20px;
  }
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px;
  flex-direction: column;
  gap: 16px;
`

const ErrorContainer = styled.div`
  text-align: center;
  padding: 40px;
  color: #ff4d4f;
`

const EmptyContainer = styled.div`
  text-align: center;
  padding: 40px;
  color: #8c8c8c;
`

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 80px;
  flex-direction: column;
  gap: 8px;
`

// Additional styled components for Practice Progress Analytics
const PracticeChartSection = styled(Card)`
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);
  margin-top: 24px;
  background: white;

  .ant-card-head {
    border-bottom: 1px solid #f0f0f0;
    background: #fafafa;
    border-radius: 8px 8px 0 0;
    padding: 12px 20px;
    min-height: auto;
  }

  .ant-card-head-title {
    font-size: 16px;
    font-weight: 600;
    color: #333333;
  }

  .ant-card-body {
    padding: 20px;
  }
`

const ChartContainer = styled.div`
  height: 350px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    height: 300px;
  }
  
  @media (max-width: 480px) {
    height: 250px;
  }
`

const EmptyChartContainer = styled.div`
  text-align: center;
  padding: 40px;
  color: #8c8c8c;
`

const SummaryTab = ({
  studentData,
  mockProgressData,
  suggestedRoles,
  chartData,
  prepareChartData,
  CustomTooltip,
  practiceScoresLoading,
  practiceScoresError,
  assessmentScoresLoading,
  assessmentScoresError,
  cefrLevelLoading,
  cefrLevelError,
  student, // Add student prop for fetching practice data
}) => {
  const dispatch = useDispatch()

  // Redux selectors for practice data
  const practiceScores = useSelector(selectPracticeScores)
  const practiceScoresLoadingState = useSelector(selectPracticeScoresLoading)
  const practiceScoresErrorState = useSelector(selectPracticeScoresError)
  const aptitudePracticeScores = useSelector(selectAptitudePracticeScores2)
  const aptitudePracticeScoresLoading = useSelector(selectAptitudePracticeScoresLoading)
  const aptitudePracticeScoresError = useSelector(selectAptitudePracticeScoresError)
  
  // DayZero selectors for practice
  const communicationDayZero = useSelector(selectCommunicationDayZero)
  const aptitudeDayZero = useSelector(selectAptitudeDayZero)

  // Debug logging for practice data
  console.log('🔍 Practice Scores:', practiceScores)
  console.log('🔍 Aptitude Practice Scores:', aptitudePracticeScores)
  console.log('🔍 Communication Day Zero:', communicationDayZero)
  console.log('🔍 Aptitude Day Zero:', aptitudeDayZero)

  // Redux selectors for actual assessment data (is_practice=false)
  const assessmentScores = useSelector(selectAssessmentScores2)
  const assessmentScoresLoadingState = useSelector(selectAssessmentScores2Loading)
  const assessmentScoresErrorState = useSelector(selectAssessmentScores2Error)
  const aptitudeAssessmentScores = useSelector(selectAptitudeAssessmentScores2)
  const aptitudeAssessmentScoresLoading = useSelector(selectAptitudeAssessmentScores2Loading)
  const aptitudeAssessmentScoresError = useSelector(selectAptitudeAssessmentScores2Error)
  
  // DayZero selectors for actual assessments
  const communicationAssessmentDayZero = useSelector(selectCommunicationAssessmentDayZero)
  const aptitudeAssessmentDayZero = useSelector(selectAptitudeAssessmentDayZero)

  // Debug logging for assessment data
  console.log('🔍 Assessment Scores:', assessmentScores)
  console.log('🔍 Assessment Scores Type:', typeof assessmentScores)
  console.log('🔍 Assessment Scores.data:', assessmentScores?.data)
  console.log('🔍 Assessment Scores.dayZero:', assessmentScores?.dayZero)
  console.log('🔍 Aptitude Assessment Scores:', aptitudeAssessmentScores)
  console.log('🔍 Aptitude Assessment Scores Type:', typeof aptitudeAssessmentScores)
  console.log('🔍 Aptitude Assessment Scores.data:', aptitudeAssessmentScores?.data)
  console.log('🔍 Aptitude Assessment Scores.dayZero:', aptitudeAssessmentScores?.dayZero)
  console.log('🔍 Communication Assessment Day Zero:', communicationAssessmentDayZero)
  console.log('🔍 Aptitude Assessment Day Zero:', aptitudeAssessmentDayZero)

  // Generate chart data function - same as PracticeTab
  const generatePracticeChartData = useCallback(() => {
    // Add proper null checks and ensure arrays for real data
    const communicationScores = Array.isArray(practiceScores?.data) ? practiceScores.data : 
                                 Array.isArray(practiceScores) ? practiceScores : []
    const aptitudeScores = Array.isArray(aptitudePracticeScores?.data) ? aptitudePracticeScores.data : 
                           Array.isArray(aptitudePracticeScores) ? aptitudePracticeScores : []

    // Create a map of all unique dates with their respective scores
    const dateScoresMap = new Map()

    // Add communication scores by date
    communicationScores.forEach((scoreData) => {
      if (scoreData && scoreData.submittedAt) {
        const date = scoreData.submittedAt
        if (!dateScoresMap.has(date)) {
          dateScoresMap.set(date, {
            date,
            communicationScore: 0,
            aptitudeScore: 0,
            hasCommunication: false,
            hasAptitude: false
          })
        }
        const entry = dateScoresMap.get(date)
        entry.communicationScore = scoreData.total_score || 0
        entry.hasCommunication = true
        entry.communicationDelta = scoreData.delta || 0
      }
    })

    // Add aptitude scores by date
    aptitudeScores.forEach((scoreData) => {
      if (scoreData && scoreData.submittedAt) {
        const date = scoreData.submittedAt
        if (!dateScoresMap.has(date)) {
          dateScoresMap.set(date, {
            date,
            communicationScore: 0,
            aptitudeScore: 0,
            hasCommunication: false,
            hasAptitude: false
          })
        }
        const entry = dateScoresMap.get(date)
        entry.aptitudeScore = scoreData.total_score || 0
        entry.hasAptitude = true
        entry.aptitudeDelta = scoreData.delta || 0
      }
    })

    // Sort by date and create sessions
    const sortedDates = Array.from(dateScoresMap.values()).sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    )

    const sessions = []

    // Start with session 0 (baseline with dayZero data)
    sessions.push({
      session: 0,
      communication_combined_progress: communicationDayZero?.total_score || 0,
      aptitude_combined_progress: aptitudeDayZero?.total_score || 0,
      submittedAt: null,
      assessmentAssignedId: null,
      delta: 0,
      type: 'dayZero'
    })

    // Process each unique date - directly use backend scores
    sortedDates.forEach((dateEntry, index) => {
      // Create session entry with direct scores from backend
      sessions.push({
        session: index + 1,
        communication_combined_progress: dateEntry.hasCommunication ? dateEntry.communicationScore : 
                                        (sessions[sessions.length - 1]?.communication_combined_progress || 0),
        aptitude_combined_progress: dateEntry.hasAptitude ? dateEntry.aptitudeScore : 
                                   (sessions[sessions.length - 1]?.aptitude_combined_progress || 0),
        submittedAt: dateEntry.date,
        assessmentAssignedId: null,
        type: dateEntry.hasCommunication && dateEntry.hasAptitude ? 'both' : 
              dateEntry.hasCommunication ? 'communication' : 'aptitude',
        delta: dateEntry.hasCommunication ? dateEntry.communicationDelta : 
               dateEntry.hasAptitude ? dateEntry.aptitudeDelta : 0
      })
    })

    return sessions
  }, [practiceScores, aptitudePracticeScores, communicationDayZero, aptitudeDayZero])

  // Generate actual assessment chart data function (is_practice=false)
  const generateAssessmentChartData = useCallback(() => {
    console.log('🔧 Generating Assessment Chart Data...')
    console.log('📊 Assessment Scores Input:', assessmentScores)
    console.log('📊 Aptitude Assessment Scores Input:', aptitudeAssessmentScores)
    
    // Add proper null checks and ensure arrays for real data
    const communicationScores = Array.isArray(assessmentScores?.data) ? assessmentScores.data : 
                                 Array.isArray(assessmentScores) ? assessmentScores : []
    const aptitudeScores = Array.isArray(aptitudeAssessmentScores?.data) ? aptitudeAssessmentScores.data : 
                           Array.isArray(aptitudeAssessmentScores) ? aptitudeAssessmentScores : []

    console.log('📊 Communication Assessment Scores Array:', communicationScores)
    console.log('📊 Aptitude Assessment Scores Array:', aptitudeScores)

    // Combine both communication and aptitude scores by date
    const dateScoresMap = new Map()

    // Add communication scores by date
    communicationScores.forEach((scoreData) => {
      if (scoreData && scoreData.submittedAt) {
        const date = scoreData.submittedAt
        if (!dateScoresMap.has(date)) {
          dateScoresMap.set(date, {
            date,
            communicationScore: 0,
            aptitudeScore: 0,
            hasCommunication: false,
            hasAptitude: false
          })
        }
        const entry = dateScoresMap.get(date)
        entry.communicationScore = scoreData.total_score || 0
        entry.hasCommunication = true
        entry.communicationDelta = scoreData.delta || 0
      }
    })

    // Add aptitude scores by date
    aptitudeScores.forEach((scoreData) => {
      if (scoreData && scoreData.submittedAt) {
        const date = scoreData.submittedAt
        if (!dateScoresMap.has(date)) {
          dateScoresMap.set(date, {
            date,
            communicationScore: 0,
            aptitudeScore: 0,
            hasCommunication: false,
            hasAptitude: false
          })
        }
        const entry = dateScoresMap.get(date)
        entry.aptitudeScore = scoreData.total_score || 0
        entry.hasAptitude = true
        entry.aptitudeDelta = scoreData.delta || 0
      }
    })

    // If we have either communication or aptitude assessment scores, process them
    if (communicationScores.length > 0 || aptitudeScores.length > 0) {
      const sessions = []
      
      // Start with session 0 (baseline with dayZero data)
      sessions.push({
        session: 0,
        communication_combined_progress: communicationAssessmentDayZero?.total_score || 
                                       assessmentScores?.dayZero?.total_score || 
                                       13.33, // Fallback from your data
        aptitude_combined_progress: aptitudeAssessmentDayZero?.total_score || 
                                  aptitudeAssessmentScores?.dayZero?.total_score ||
                                  0, // Fallback for aptitude
        submittedAt: null,
        assessmentAssignedId: null,
        delta: 0,
        type: 'dayZero'
      })

      // Sort by date and create sessions
      const sortedDates = Array.from(dateScoresMap.values()).sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      )

      // Process each unique date - directly use backend scores
      sortedDates.forEach((dateEntry, index) => {
        sessions.push({
          session: index + 1,
          communication_combined_progress: dateEntry.hasCommunication ? dateEntry.communicationScore : 
                                          (sessions[sessions.length - 1]?.communication_combined_progress || 0),
          aptitude_combined_progress: dateEntry.hasAptitude ? dateEntry.aptitudeScore : 
                                     (sessions[sessions.length - 1]?.aptitude_combined_progress || 0),
          submittedAt: dateEntry.date,
          assessmentAssignedId: null,
          type: dateEntry.hasCommunication && dateEntry.hasAptitude ? 'both' : 
                dateEntry.hasCommunication ? 'communication' : 'aptitude',
          delta: dateEntry.hasCommunication ? dateEntry.communicationDelta : 
                 dateEntry.hasAptitude ? dateEntry.aptitudeDelta : 0
        })
      })

      console.log('📊 Assessment Sessions Generated (with data):', sessions)
      return sessions
    }

    console.log('📊 No assessment data found, returning empty array')
    return []
  }, [assessmentScores, aptitudeAssessmentScores, communicationAssessmentDayZero, aptitudeAssessmentDayZero])

  // Prepare chart data for rendering
  const preparePracticeChartData = useCallback(() => {
    try {
      const chartData = generatePracticeChartData()
      console.log('🔍 Practice Chart Data:', chartData)
      return chartData.map((session) => ({
        session: session.session,
        "Communication Practice": session.communication_combined_progress || 0,
        "Aptitude Practice": session.aptitude_combined_progress || 0,
      }))
    } catch (error) {
      console.error("Error preparing practice chart data:", error)
      return []
    }
  }, [generatePracticeChartData])

  // Prepare actual assessment chart data for rendering
  const prepareAssessmentChartData = useCallback(() => {
    try {
      const chartData = generateAssessmentChartData()
      console.log('🔍 Assessment Chart Data Raw:', chartData)
      
      // If no assessment data, create mock data to test both lines
      if (!chartData || chartData.length === 0) {
        // console.log('⚠️ No assessment data found, creating mock data for testing')
        return [
          // { session: 0, "Communication Assessment": 13.33, "Aptitude Assessment": 20 },
          // { session: 1, "Communication Assessment": 15.78, "Aptitude Assessment": 25 },
          // { session: 2, "Communication Assessment": 13.6, "Aptitude Assessment": 30 },
          // { session: 3, "Communication Assessment": 18.66, "Aptitude Assessment": 35 },
          // { session: 4, "Communication Assessment": 12.57, "Aptitude Assessment": 28 },
        ]
      }
      
      const mappedData = chartData.map((session) => ({
        session: session.session,
        "Communication Assessment": session.communication_combined_progress || 0,
        "Aptitude Assessment": session.aptitude_combined_progress || 0,
      }))
      
      console.log('🔍 Assessment Chart Data Mapped:', mappedData)
      return mappedData
    } catch (error) {
      console.error("Error preparing assessment chart data:", error)
      // Return mock data on error for testing
      return [
        // { session: 0, "Communication Assessment": 13.33, "Aptitude Assessment": 20 },
        // { session: 1, "Communication Assessment": 15.78, "Aptitude Assessment": 25 },
      ]
    }
  }, [generateAssessmentChartData])

  // Custom tooltip component for combined progress
  const CombinedCustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: "white",
            border: "1px solid #d9d9d9",
            borderRadius: "6px",
            padding: "12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          <p
            style={{
              fontWeight: "600",
              margin: "0 0 8px 0",
              color: "#262626",
              fontSize: "14px",
            }}
          >
            {`Session ${label}`}
          </p>
          {payload.map((entry, index) => {
            // Only show entries that have actual data (not null)
            if (entry.value !== null && entry.value !== undefined) {
              return (
                <p
                  key={index}
                  style={{
                    margin: "4px 0",
                    color: entry.color,
                    fontSize: "13px",
                    fontWeight: "500",
                  }}
                >
                  {`${entry.dataKey}: ${entry.value.toFixed(1)}%`}
                </p>
              )
            }
            return null
          })}
        </div>
      )
    }
    return null
  }

  // Fetch practice data on component mount
  useEffect(() => {
    if (student?.email) {
      console.log('🚀 SummaryTab: Fetching practice data for:', student.email)
      
      dispatch(fetchPracticeScores(student.email)).catch((error) => {
        console.log("Failed to load practice scores:", error)
      })
      dispatch(fetchAptitudePracticeScores(student.email)).catch((error) => {
        console.log("Failed to load aptitude practice scores:", error)
      })
    }
  }, [student?.email, dispatch])

  // Fetch assessment data on component mount
  useEffect(() => {
    if (student?.email) {
      console.log('🚀 SummaryTab: Fetching assessment data for:', student.email)
      
      console.log('📡 Calling fetchPracticeScores2 (Communication Assessment)...')
      dispatch(fetchPracticeScores2(student.email)).then((result) => {
        console.log('✅ fetchPracticeScores2 result:', result)
      }).catch((error) => {
        console.log("❌ Failed to load assessment scores:", error)
      })
      
      console.log('📡 Calling fetchAptitudePracticeScores2 (Aptitude Assessment)...')
      dispatch(fetchAptitudePracticeScores2(student.email)).then((result) => {
        console.log('✅ fetchAptitudePracticeScores2 result:', result)
      }).catch((error) => {
        console.log("❌ Failed to load aptitude assessment scores:", error)
      })
    }
  }, [student?.email, dispatch])

  // Check loading and error states for practice data
  const isPracticeLoading = practiceScoresLoadingState || aptitudePracticeScoresLoading
  const hasPracticeError = practiceScoresErrorState || aptitudePracticeScoresError
  const practiceErrorMessage = practiceScoresErrorState || aptitudePracticeScoresError

  // Check loading and error states for assessment data
  const isAssessmentLoading = assessmentScoresLoading || aptitudeAssessmentScoresLoading
  const hasAssessmentError = assessmentScoresError || aptitudeAssessmentScoresError
  const assessmentErrorMessage = assessmentScoresError || aptitudeAssessmentScoresError

  // Check if components should show chart data
  const hasPracticeChartData = generatePracticeChartData().length > 0
  const hasAssessmentChartData = generateAssessmentChartData().length > 0

  // Original loading and error states
  const isLoading = practiceScoresLoading || assessmentScoresLoading
  const hasError = practiceScoresError || assessmentScoresError
  const chartDataArray = prepareChartData()

  const getProgressColor = (score) => {
    if (score >= 70) return "#0FB680"
    if (score >= 50) return "#3249D7"
    return "#F54B56"
  }

  return (
    <TabContent>
      {/* <Row gutter={[16, 16]}>
        <Col span={24}>
          <SectionCard title="Suggested Roles" size="small">
            <RolePillsContainer>
              {suggestedRoles.map((role, index) => (
                <RolePill key={index}>{role}</RolePill>
              ))}
            </RolePillsContainer>
          </SectionCard>
        </Col>

        <Col span={24}>
          <SectionCard>
            <ReadinessSection>
              <ReadinessCircle score={studentData.readinessScore}>
                <ReadinessText>
                  <ReadinessScore>{studentData.readinessScore}%</ReadinessScore>
                  <ReadinessStatus ready={studentData.isReady}>
                    {studentData.isReady ? "Ready" : "Not Ready"}
                  </ReadinessStatus>
                </ReadinessText>
              </ReadinessCircle>
            </ReadinessSection>
          </SectionCard>
        </Col>
      </Row> */}

      <SkillsGrid>
        <SkillCard>
          <SkillTitle>Communication</SkillTitle>
          {cefrLevelLoading ? (
            <LoadingWrapper>
              <Spin size="small" />
              <Text style={{ fontSize: "10px", color: "#8c8c8c" }}>Loading CEFR...</Text>
            </LoadingWrapper>
          ) : cefrLevelError ? (
            <div style={{ textAlign: "center", padding: "12px 0" }}>
              <Text type="danger" style={{ fontSize: "11px" }}>
                Error loading CEFR
              </Text>
            </div>
          ) : (
            <>
              <Progress
                type="circle"
                percent={studentData.readinessScore}
                strokeColor={getProgressColor(studentData.readinessScore)}
                size={60}
                strokeWidth={6}
              />
              <SkillLevel>Level {studentData.communication.level}</SkillLevel>
              {studentData.communication.improvement > 0 && (
                <SkillImprovement positive>+{studentData.communication.improvement}</SkillImprovement>
              )}
            </>
          )}
        </SkillCard>

        <SkillCard>
          <SkillTitle>Aptitude</SkillTitle>
          <Progress
            type="circle"
            percent={studentData.aptitude?.score || 0}
            strokeColor={getProgressColor(studentData.aptitude?.score || 0)}
            size={60}
            strokeWidth={6}
          />
          <SkillLevel>Level {studentData.aptitude?.level || "N/A"}</SkillLevel>
          {studentData.aptitude?.improvement > 0 && (
            <SkillImprovement positive>+{studentData.aptitude?.improvement}</SkillImprovement>
          )}
        </SkillCard>

      
      </SkillsGrid>

    

      {/* Progress Over Time Analytics - Combined Practice & Assessment */}
      <PracticeChartSection title="Progress Over Time Analytics">
        {(isPracticeLoading || isAssessmentLoading) ? (
          <LoadingContainer>
            <Spin size="large" />
            <Text style={{ color: "#8c8c8c" }}>Loading progress data...</Text>
          </LoadingContainer>
        ) : (hasPracticeError && hasAssessmentError) ? (
          <ErrorContainer>
            <Alert
              message="Error Loading Progress Data"
              description={`Failed to load progress data: ${practiceErrorMessage || assessmentErrorMessage}`}
              type="error"
              showIcon
            />
          </ErrorContainer>
        ) : (!hasPracticeChartData && !hasAssessmentChartData) ? (
          <EmptyChartContainer>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No progress data available yet. Complete some practice sessions or assessments to see your progress!"
            />
          </EmptyChartContainer>
        ) : (
          <ChartErrorBoundary>
            <ChartContainer>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart 
                  data={(() => {
                    // Get separate data for practice and assessment
                    const practiceData = preparePracticeChartData()
                    const assessmentData = prepareAssessmentChartData()
                    
                    console.log('🔍 Final Practice Data:', practiceData)
                    console.log('🔍 Final Assessment Data:', assessmentData)
                    
                    // Create a comprehensive session range
                    const maxPracticeSessions = practiceData.length
                    const maxAssessmentSessions = assessmentData.length
                    const totalSessions = Math.max(maxPracticeSessions, maxAssessmentSessions)
                    
                    // Build chart data with separate data series
                    const chartData = []
                    for (let i = 0; i < totalSessions; i++) {
                      const practicePoint = practiceData[i] || {}
                      const assessmentPoint = assessmentData[i] || {}
                      
                      chartData.push({
                        session: i,
                        "Communication Practice": practicePoint["Communication Practice"] || null,
                        "Aptitude Practice": practicePoint["Aptitude Practice"] || null,
                        "Communication Assessment": assessmentPoint["Communication Assessment"] || null,
                        "Aptitude Assessment": assessmentPoint["Aptitude Assessment"] || null
                      })
                    }
                    
                    console.log('🔍 Final Combined Chart Data:', chartData)
                    return chartData
                  })()}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid stroke="#f0f0f0" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="session"
                    type="number"
                    domain={["dataMin", "dataMax"]}
                    tickCount={Math.min(Math.max(generatePracticeChartData().length, generateAssessmentChartData().length), 10)}
                    stroke="#8c8c8c"
                    fontSize={12}
                    tickLine={false}
                    axisLine={{ stroke: "#d9d9d9", strokeWidth: 1 }}
                    label={{ 
                      value: 'Sessions', 
                      position: 'insideBottom', 
                      offset: -10, 
                      style: { textAnchor: 'middle', fill: '#595959', fontSize: 12, fontWeight: 'bold' } 
                    }}
                  />
                  <YAxis
                    domain={[0, 100]}
                    stroke="#8c8c8c"
                    fontSize={12}
                    tickLine={false}
                    axisLine={{ stroke: "#d9d9d9", strokeWidth: 1 }}
                    label={{ 
                      value: 'Scores (%)', 
                      angle: -90, 
                      position: 'insideLeft', 
                      style: { textAnchor: 'middle', fill: '#595959', fontSize: 12, fontWeight: 'bold' } 
                    }}
                  />
                  <Tooltip content={<CombinedCustomTooltip />} />
                  <Legend
                    verticalAlign="top"
                    align="right"
                    iconType="line"
                    wrapperStyle={{ fontSize: 12, fontWeight: 500 }}
                  />
                  
                  {/* Practice Lines (Dotted) */}
                  <Line
                    type="monotone"
                    dataKey="Communication Practice"
                    stroke="#52c41a"
                    strokeWidth={3}
                    strokeDasharray="5 5"
                    dot={{ fill: "#52c41a", strokeWidth: 2, r: 4 }}
                    activeDot={{
                      r: 6,
                      fill: "#52c41a",
                      stroke: "#ffffff",
                      strokeWidth: 2,
                    }}
                    name="Communication Practice"
                    connectNulls={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="Aptitude Practice"
                    stroke="#1890ff"
                    strokeWidth={3}
                    strokeDasharray="8 4"
                    dot={{ fill: "#1890ff", strokeWidth: 2, r: 4 }}
                    activeDot={{
                      r: 6,
                      fill: "#1890ff",
                      stroke: "#ffffff",
                      strokeWidth: 2,
                    }}
                    name="Aptitude Practice"
                    connectNulls={false}
                  />
                  
                  {/* Assessment Lines (Solid) */}
                  <Line
                    type="monotone"
                    dataKey="Communication Assessment"
                    stroke="#52c41a"
                    strokeWidth={4}
                    strokeDasharray="none"
                    dot={{ fill: "#52c41a", strokeWidth: 2, r: 5 }}
                    activeDot={{
                      r: 8,
                      fill: "#52c41a",
                      stroke: "#ffffff",
                      strokeWidth: 3,
                    }}
                    name="Communication Assessment"
                    connectNulls={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="Aptitude Assessment"
                    stroke="#1890ff"
                    strokeWidth={4}
                    strokeDasharray="none"
                    dot={{ fill: "#1890ff", strokeWidth: 2, r: 5 }}
                    activeDot={{
                      r: 8,
                      fill: "#1890ff",
                      stroke: "#ffffff",
                      strokeWidth: 3,
                    }}
                    name="Aptitude Assessment"
                    connectNulls={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          </ChartErrorBoundary>
        )}
      </PracticeChartSection>
    </TabContent>
  )
}

export default SummaryTab
